import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { AppSettings, defaultAppSettings } from '@/types/appSettings';
import { getAppSettings, updateAppSettings } from '@/services/projects/appSettingsService';

export const useAppSettingsForm = () => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const [settings, setSettings] = useState<AppSettings>({
    ...defaultAppSettings
  });
  const [originalSettings, setOriginalSettings] = useState<AppSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Partial<AppSettings>>({});

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAppSettings();
      
      if (data) {
        const settingsData = {
          ...data,
          games_allowed_per_day: data.games_allowed_per_day || 3
        };
        
        setSettings(settingsData);
        setOriginalSettings(settingsData);
        
        // Only reset form dirty state after initial load or after a successful save
        if (!isFormDirty || saveSuccess) {
          setIsFormDirty(false);
          setSaveSuccess(false);
          setPendingChanges({});
        }
      }
    } catch (error) {
      console.error('Error fetching app settings:', error);
      toast({
        title: t('admin.error', 'Error'),
        description: t('admin.errorFetchingSettings', 'Error fetching application settings'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast, isFormDirty, saveSuccess]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSettingsChange = (field: string, value: string | number | null) => {
    console.log(`Setting field ${field} to value:`, value);
    setIsFormDirty(true);
    setPendingChanges(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update the settings object for UI rendering
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Check that we have pending changes
      if (Object.keys(pendingChanges).length === 0) {
        toast({
          title: t('admin.info', 'Information'),
          description: t('admin.noChangesToSave', 'No changes to save'),
          variant: 'default',
        });
        setSaving(false);
        return;
      }
      
      console.log('Saving settings with pending changes:', pendingChanges);
      
      // Create update object with current values from pending changes
      const updateData: Partial<AppSettings> = {};
      
      if (pendingChanges.app_name !== undefined) updateData.app_name = pendingChanges.app_name;
      if (pendingChanges.logo_url !== undefined) updateData.logo_url = pendingChanges.logo_url;
      if (pendingChanges.favicon_url !== undefined) updateData.favicon_url = pendingChanges.favicon_url;
      if (pendingChanges.social_preview_url !== undefined) updateData.social_preview_url = pendingChanges.social_preview_url;
      if (pendingChanges.games_allowed_per_day !== undefined) updateData.games_allowed_per_day = pendingChanges.games_allowed_per_day;
      
      const updatedSettings = await updateAppSettings(updateData);
      
      toast({
        title: t('admin.success', 'Success'),
        description: t('admin.settingsSaved', 'Settings saved successfully'),
        variant: 'default',
      });
      
      // Update document title and favicon
      if (pendingChanges.app_name) {
        document.title = pendingChanges.app_name as string;
      }
      
      if (pendingChanges.favicon_url) {
        const faviconLink = document.querySelector("link[rel='icon']");
        if (faviconLink && faviconLink instanceof HTMLLinkElement) {
          faviconLink.href = pendingChanges.favicon_url as string;
        }
      }
      
      // Store in localStorage for quick reference
      if (pendingChanges.app_name) {
        localStorage.setItem('app_name', pendingChanges.app_name as string);
      }
      
      if (pendingChanges.logo_url) {
        localStorage.setItem('app_logo_url', pendingChanges.logo_url as string);
      }
      
      if (pendingChanges.favicon_url) {
        localStorage.setItem('app_favicon_url', pendingChanges.favicon_url as string);
      }
      
      // Reset the form dirty flag and pending changes after successful save
      setSaveSuccess(true);
      setIsFormDirty(false);
      setPendingChanges({});
      
      // Refresh settings to ensure we have latest data
      fetchSettings();
    } catch (error) {
      console.error('Error saving app settings:', error);
      toast({
        title: t('admin.error', 'Error'),
        description: t('admin.errorSavingSettings', 'Error saving application settings'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return Object.keys(pendingChanges).length > 0;
  };

  return {
    settings,
    loading,
    saving,
    handleSettingsChange,
    saveSettings,
    isFormDirty,
    saveSuccess,
    hasChanges,
    pendingChanges
  };
};
